import React from 'react';
import { Box, Typography, TextField, MenuItem, Divider, Button, CircularProgress } from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PrintIcon from '@material-ui/icons/Print';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { paymentThunks, paymentActions } from 'src/store/slices/paymentSlice';
import { printProformaInvoice } from './ProformaInvoice';
import { getUser } from 'src/utils/Common';
const DEMO_PRODUCT_NAMES = {
  'ABC123': 'B5WF U78418 WB',
  'DEF456': 'B3WT 71011 XS',
  'GHI789': 'B7WF 96243 MB',
  'SKU001': 'B5WF U78418 WB',
  'SKU002': 'B3WF 74210 WB'
};

const getDemoName = sku => DEMO_PRODUCT_NAMES[sku] || (sku ? 'B5WF U78418 WB' : '—');

const PaymentInfo = ({
  currency,
  productLink,
  customerName,
  customerSurname,
  customerPhone,
  customerEmail,
  city,
  street,
  postCode,
  addressFree,
  proceedToPaymentDisabled,
  onProformaGenerated
}) => {
  // Redux product/workorder
  const dispatch = useDispatch();
  const SessionUser = getUser();
  const productName = useSelector(s => s.payment.productName);
  const sku = useSelector(s => s.payment.productSKU);
  const productId = useSelector(s => s.payment.productId); // added
  const price = useSelector(s => s.payment.productPrice);
  const workOrderId = useSelector(s => s.payment.workOrderId);
  const cuiNumber = useSelector(s => s.payment.cuiNumber);
  const paymentSessionData = useSelector(s => s.payment.paymentSessionData);
  const paymentUrlFromSlice = useSelector(s => s.payment.paymentUrl);
  const globalLoading = useSelector(s => s.payment.loading) === 'Loading';
  const paymentInitiated = useSelector(s => s.payment.paymentInitiated);
  // New: product order creation state
  const createdProductOrderId = useSelector(s => s.payment.createdProductOrderId);
  const createProductOrderLoading = useSelector(s => s.payment.createProductOrderLoading) === 'Loading';
  // New: take verifiedCustomerId from slice
  const verifiedCustomerId = useSelector(s => s.payment.verifiedCustomerId);
  // Existing: consumer id from created customer (supports ResponseDto<ConsumerDto> or plain ConsumerDto)
  const consumerId = useSelector(s => {
    const cc = s.payment.createdCustomer;
    return (cc && (cc.ResponseObject?.Id ?? cc.Id)) || null;
  });
  const history = useHistory();

  // Local payment UI states
  const [paymentError, setPaymentError] = React.useState('');
  const [paymentCompleted, setPaymentCompleted] = React.useState(false);
  const [paymentSuccessMessage, setPaymentSuccessMessage] = React.useState('');
  const [showPaymentIframe, setShowPaymentIframe] = React.useState(false);
  const [paymentUrl, setPaymentUrl] = React.useState('');
  const [waitingForPayment, setWaitingForPayment] = React.useState(false);
  const [proformaPrinted, setProformaPrinted] = React.useState(false);
  const [generatingProforma, setGeneratingProforma] = React.useState(false);
  const [selectedPosDevice, setSelectedPosDevice] = React.useState('');

  // POS Matching state
  const employeePosMatching = useSelector(s => s.payment.employeePosMatching) || [];
  const employeePosMatchingLoading = useSelector(s => s.payment.employeePosMatchingLoading) === 'Loading';

  // Prevent auto-opening: clear previous session/url and mark not initiated on mount
  React.useEffect(() => {
    dispatch(paymentActions.setPaymentInitiated(false));
    dispatch(paymentActions.clearPaymentData());
    setShowPaymentIframe(false);
    setWaitingForPayment(false);
    setPaymentUrl('');
    setPaymentError('');
  }, [dispatch]);

  // Oturum verisi değişince karar ver (only react if user pressed Proceed)
  React.useEffect(() => {
    setShowPaymentIframe(false);
    if (!paymentInitiated) {
      // Ignore session updates until user explicitly initiates payment
      setWaitingForPayment(false);
      setPaymentUrl('');
      return;
    }
    if (!paymentSessionData) return;
    if (paymentSessionData.HasApplicationSingleProvider) {
      // Single provider: link sent scenario
      setPaymentCompleted(true);
      setPaymentUrl('');
      dispatch(paymentActions.setWorkOrderId(''));
      if (paymentActions.setPaymentUrl) {
        dispatch(paymentActions.setPaymentUrl(null));
      }
      setPaymentSuccessMessage('Payment link has been delivered to the customer.');
      setShowPaymentIframe(false);
      setWaitingForPayment(false);
      setPaymentError('');
    } else {
      // Multiple providers / iframe payment
      if (paymentUrlFromSlice) {
        setPaymentUrl(paymentUrlFromSlice);
        setShowPaymentIframe(true);
        setWaitingForPayment(false);
        setPaymentError('');
      } else {
        // URL missing: show waiting state only after proceed
        setWaitingForPayment(true);
      }
    }
  }, [paymentSessionData, paymentUrlFromSlice, paymentInitiated, dispatch]);

  const handleRetryPayment = () => {
    if (!price) return;
    setPaymentError('');
    setPaymentCompleted(false);
    setPaymentSuccessMessage('');
    setShowPaymentIframe(false);
    setPaymentUrl('');
    setWaitingForPayment(false);
    // Get selected POS device name
    const selectedPos = employeePosMatching.find(p => (p.Id || p.PosId) === selectedPosDevice);
    const posUserRef = selectedPos?.PosMachineIdentifier || String(selectedPosDevice) || "john.doe";
    const req = {
      ApplicationReference: String(createdProductOrderId ?? ''), // ensure string
      Amount: price,
      Currency: currency || 'RON',
      UserRef: posUserRef,
      cuiNumber: cuiNumber || null,
      Products:[{
        Um:"pcs",
        Name: ((sku || '') + ' ' + (productName || '')).substring(0, 40),
        Price: price,
        VatId: "C",
        Quantity:"1",
        ProductId : productId || 0,
        ProductSku: sku || ''
      }]
    };
    console.log('[ProceedToPayment] Creating payment session...1');
    dispatch(paymentThunks.ProductPreSale(req))
      .then(r => {
        if (r.payload?.error) {
          setPaymentError('Retry failed: ' + (r.payload.error.Message || 'Unknown'));
        }
      })
      .catch(e => setPaymentError('Retry exception: ' + e.message));
  };

  // New: Create Product Order
  const handleCreateProductOrder = () => {
    if (!sku || !price) return;
    // Get selected POS device name
    const selectedPos = employeePosMatching.find(p => (p.Id || p.PosId) === selectedPosDevice);
    const posUserRef = selectedPos?.PosMachineIdentifier || String(selectedPosDevice) || "john.doe";
    const dto = {
      ApplicationReference: workOrderId || null, // changed from 'NO_WORK_ORDER' to null
      Amount: price,
      Currency: currency || 'RON',
      UserRef: posUserRef,
      // Prefer verifiedCustomerId, fallback to created consumerId
      ConsumerId: (verifiedCustomerId ?? consumerId ?? 0),
      Products: [{
        Um: "pcs",
        Name: ((sku || '') + ' ' + (productName || '')).substring(0, 40),
        Price: price,
        VatId: "C",
        Quantity: "1",
        ProductId: productId || 0,
        ProductSku: sku || ''
      }]
    };
    dispatch(paymentThunks.CreateProductOrder(dto))
      .then(r => {
        console.log('[CreateProductOrder] result:', r?.payload);
      })
      .catch(e => {
        console.error('[CreateProductOrder] error:', e);
      });
  };

  const handlePaymentCancelled = () => {
    setShowPaymentIframe(false);
    setWaitingForPayment(false);
    setPaymentUrl('');
    setPaymentError('Payment cancelled by user.');
  };

  const handlePaymentCompleted = () => {
    setPaymentCompleted(true);
    setShowPaymentIframe(false);
    setWaitingForPayment(false);
    setPaymentSuccessMessage('Payment completed successfully.');
  };

  const handleManualCompletePayment = () => {
    // manual completion + reset
    setPaymentCompleted(false);
    setShowPaymentIframe(false);
    setWaitingForPayment(false);
    setPaymentError('');
    setPaymentUrl('');
    dispatch(paymentActions.setWorkOrderId(''));
    if (paymentActions.clearProductSelection) {
      // reset selected product (SKU / Price)
      dispatch(paymentActions.clearProductSelection());
    } else {
      // fallback if clearProductSelection not available
      paymentActions.setProductSKU && dispatch(paymentActions.setProductSKU(''));
      paymentActions.setProductPrice && dispatch(paymentActions.setProductPrice(''));
    }
    if (paymentActions.setPaymentUrl) {
      dispatch(paymentActions.setPaymentUrl(null));
    }
    if (paymentActions.setPaymentInitiated) {
      dispatch(paymentActions.setPaymentInitiated(false));
    }
    history.push('/Sales');
  };

  const handleGenerateProforma = () => {
    setGeneratingProforma(true);
    setTimeout(() => {
      try {
        printProformaInvoice({
          sku,
          productName,
          price,
          currency,
          customerName,
          customerSurname,
          customerEmail,
          customerPhone,
          cuiNumber,
          city,
          street,
          postCode,
          addressFree,
          workOrderId,
          vatRate: 21,       // added
          quantity: 1,        // added (static for now)
          productOrderId: createdProductOrderId || null // New: include created product order id
        });

        setGeneratingProforma(false);
        setProformaPrinted(true);
        if (onProformaGenerated) {
          onProformaGenerated();
        }
      } catch (error) {
        console.error('Proforma generation error:', error);
        setGeneratingProforma(false);
      }
    }, 500);
  };

  const chipStyle = {
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 12px',
    background: 'linear-gradient(135deg,#ffffff,#f4f8fc)',
    border: '1px solid #c9d8e6',
    borderRadius: 14,
    color: '#0f3254',
    boxShadow: '0 1px 2px rgba(0,0,0,.06)'
  };

  // Payment status block
  const renderPaymentState = () => {
    if (paymentError) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="280px">
          <Typography variant="h6" gutterBottom align="center" color="error">Payment Error</Typography>
          <Typography variant="body1" align="center" color="error" style={{ marginBottom: 16 }}>
            {paymentError}
          </Typography>
          <Box>
            <Button variant="contained" color="primary" onClick={handleRetryPayment} style={{ marginRight: 8 }}>
              Retry Payment
            </Button>
          </Box>
        </Box>
      );
    }
    if (paymentCompleted) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="280px">
          <CheckCircleOutlineIcon style={{ fontSize: 72, color: 'green', marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom align="center" color="primary">
            Payment Operation Completed Successfully!
          </Typography>
          <Typography variant="body1" align="center" color="textSecondary">
            {paymentSuccessMessage}
          </Typography>
        </Box>
      );
    }
    if (showPaymentIframe && paymentUrl) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom align="center">
            Complete Your Payment
          </Typography>
          <Box display="flex" justifyContent="center" mb={2}>
            <Button variant="outlined" color="secondary" onClick={handlePaymentCancelled} size="small">
              Cancel Payment
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleManualCompletePayment}
              size="small"
              style={{ marginLeft: 8 }}
            >
              Complete Payment
            </Button>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <iframe
              src={paymentUrl}
              style={{
                width: '100%',
                height: '500px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 4px 16px -6px rgba(0,0,0,.18)'
              }}
              title="Payment"
            />
          </Box>
        </Box>
      );
    }
    if (waitingForPayment || globalLoading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="280px">
          <CircularProgress size={60} style={{ marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom align="center">
            Payment Processing...
          </Typography>
          <Typography variant="body1" align="center" color="textSecondary">
            Please wait while we process your payment.
          </Typography>
          <Typography variant="caption" align="center" color="textSecondary" style={{ marginTop: 8 }}>
            You will be automatically redirected upon completion.
          </Typography>
          <Box mt={2}>
            <Button variant="outlined" onClick={handlePaymentCancelled}>Cancel</Button>
          </Box>
        </Box>
      );
    }
    // Preparation phase
    // return (
    //   <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="180px">
    //     <Typography variant="h6" gutterBottom align="center">Preparing Payment...</Typography>
    //     <CircularProgress />
    //   </Box>
    // );
  };

  // Force fresh product order each time page mounts
  React.useEffect(() => {
    dispatch(paymentActions.clearCreatedProductOrder());
    setProformaPrinted(false);
  }, [dispatch]);

  // Fetch POS devices for employee (using hardcoded employeeId for now)
  React.useEffect(() => {
    dispatch(paymentThunks.GetEmployeePosMatching(SessionUser.TechnicianId));
  }, [dispatch]);

  return (
    <Box mt={3} display="flex" flexDirection="column" gridGap={28}>
      {/* If payment not initiated show only selection cards */}
      {(!paymentInitiated) && (
        <div>
          {/* Selected Product Card */}
          <Box
            style={{
              position: 'relative',
              padding: '22px 24px 20px',
              borderRadius: 22,
              background: 'linear-gradient(140deg,rgba(250,252,255,.9),rgba(235,244,255,.75))',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              border: '1px solid rgba(160,190,220,.55)',
              boxShadow: '0 4px 10px -2px rgba(25,55,110,.15),0 2px 3px -1px rgba(25,55,110,.08)',
              overflow: 'hidden',
              transition: 'box-shadow .35s ease, transform .35s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 8px 26px -6px rgba(25,55,110,.28)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow =
                '0 4px 10px -2px rgba(25,55,110,.15),0 2px 3px -1px rgba(25,55,110,.08)';
              e.currentTarget.style.transform = 'none';
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 5,
                background: 'linear-gradient(90deg,#3b82f6,#60a5fa,#93c5fd)'
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(circle at 85% 18%,rgba(120,170,255,.25),rgba(120,170,255,0) 65%)',
                pointerEvents: 'none'
              }}
            />
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 700,
                fontSize: 12.5,
                letterSpacing: '.65px',
                textTransform: 'uppercase',
                color: '#2c5c9e',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <LaunchIcon style={{ fontSize: 16, color: '#2c5c9e' }} /> Selected Product
            </Typography>
            <Box display="flex" flexWrap="wrap" gridGap={8} style={{ marginBottom: 8 }}>
              <span style={chipStyle}>{sku ? `${sku} – ${productName}` : 'No Product'}</span>
              <span style={chipStyle}>Price: {price ? `${price} ${currency}` : '—'}</span>
              {workOrderId && <span style={chipStyle}>WO: {workOrderId}</span>}
            </Box>
            {productLink && (
              <div style={{ marginTop: 10 }}>
                <a
                  href={productLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: 'none',
                    color: '#1976d2',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  Product Link <LaunchIcon style={{ fontSize: 14, marginLeft: 4 }} />
                </a>
              </div>
            )}
          </Box>

          {/* Selected Customer */}
          <Box
            style={{
              position: 'relative',
              padding: '20px 22px 18px',
              borderRadius: 20,
              background: 'linear-gradient(145deg,#ffffff,#f5f9fc)',
              border: '1px solid #d5e2ed',
              boxShadow: '0 2px 8px -1px rgba(15,40,70,.08)',
              transition: 'box-shadow .35s ease, transform .35s ease',
              marginTop: 16
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 6px 18px -4px rgba(25,55,110,.25)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = '0 2px 8px -1px rgba(15,40,70,.08)';
              e.currentTarget.style.transform = 'none';
            }}
          >
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 700,
                fontSize: 12.5,
                letterSpacing: '.65px',
                textTransform: 'uppercase',
                color: '#2c5c9e',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <PersonOutlineIcon style={{ fontSize: 16, color: '#2c5c9e' }} /> Selected Customer
            </Typography>
            <Box display="flex" flexDirection="column" gridGap={8}>
              <span style={chipStyle}>{customerName || ''} {customerSurname || ''}</span>
              {customerEmail && <span style={chipStyle}>Email: {customerEmail}</span>}
              {customerPhone && <span style={chipStyle}>Phone: {customerPhone}</span>}
              {city && <span style={chipStyle}>City: {city}</span>}
              {street && <span style={chipStyle}>Street: {street}</span>}
              {postCode && <span style={chipStyle}>Post Code: {postCode}</span>}
              {addressFree && <span style={chipStyle}>Address: {addressFree}</span>}
            </Box>
          </Box>

          {/* POS Device Selection */}
          <Box
            style={{
              position: 'relative',
              padding: '20px 22px 18px',
              borderRadius: 20,
              background: 'linear-gradient(145deg,#ffffff,#fcf9f5)',
              border: '1px solid #e2d5d5',
              boxShadow: '0 2px 8px -1px rgba(70,40,15,.08)',
              transition: 'box-shadow .35s ease, transform .35s ease',
              marginTop: 16
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 6px 18px -4px rgba(110,55,25,.25)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = '0 2px 8px -1px rgba(70,40,15,.08)';
              e.currentTarget.style.transform = 'none';
            }}
          >
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 700,
                fontSize: 12.5,
                letterSpacing: '.65px',
                textTransform: 'uppercase',
                color: '#9e5c2c',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <CreditCardIcon style={{ fontSize: 16, color: '#9e5c2c' }} /> Select POS Device
            </Typography>
            <Box>
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                value={selectedPosDevice}
                onChange={(e) => {
                  setSelectedPosDevice(e.target.value);
                  dispatch(paymentActions.setSelectedPosDeviceId(e.target.value));
                }}
                disabled={employeePosMatchingLoading || employeePosMatching.length === 0}
                placeholder="Select a POS device"
                style={{ backgroundColor: '#fff', borderRadius: 8 }}
                helperText={
                  employeePosMatchingLoading
                    ? 'Loading POS devices...'
                    : employeePosMatching.length === 0
                    ? 'No POS devices available'
                    : 'Please select a POS device to proceed'
                }
              >
                {employeePosMatching.map((pos) => (
                  <MenuItem key={pos.Id} value={pos.Id}>
                    {pos.PosMachineIdentifier} ({pos.PosMachineTypeName})
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          {/* New: Create Product Order Button + status */}
          <Box mt={2} display="flex" justifyContent="center" gridGap={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateProductOrder}
              disabled={createProductOrderLoading || !sku || !price || !selectedPosDevice}
              style={{ textTransform: 'none', fontWeight: 600, padding: '10px 24px', borderRadius: 12 }}
            >
              {createProductOrderLoading ? 'Creating...' : 'Create Product Order'}
            </Button>
          </Box>
          {createdProductOrderId && (
            <Box mt={1} display="flex" justifyContent="center">
              <Typography variant="caption" color="primary">
                Created Product Order Id: {createdProductOrderId}
              </Typography>
            </Box>
          )}

          {/* Proforma Print Button (disabled until product order created) */}
          <Box mt={2} display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="secondary"
              startIcon={generatingProforma ? <CircularProgress size={16} /> : <PrintIcon />}
              onClick={handleGenerateProforma}
              disabled={generatingProforma || !createdProductOrderId}
              style={{
                textTransform: 'none',
                fontWeight: 600,
                padding: '10px 24px',
                borderRadius: 12
              }}
            >
              {proformaPrinted ? 'Proforma Printed' : 'Print Proforma Invoice'}
            </Button>
          </Box>
        </div>
      )}

      {/* If payment initiated show status block */}
      {paymentInitiated && renderPaymentState()}
    </Box>
  );
};

export default PaymentInfo;
